import { createElement, Fragment } from 'react'

import { PageSeoTagType, type Page } from '@bettermode/common/gql/generated'

interface props {
  title: string
  description?: string
  additionalMeta?: Page['seoDetail']['additionalMeta']
}

// This is not a real component by definition, but a function that returns an array of JSX elements
// The issue with returning a JSX element directly is that it can only return a single element
// and the Helmet library has a known issue where we need to feed it an array of elements
// https://github.com/nfl/react-helmet/issues/326
export const getSeoMetaTags = ({
  title,
  description,
  additionalMeta,
}: props): Array<JSX.Element> => {
  if (!title) {
    return []
  }

  const additionalMetaHasTitle = additionalMeta?.some(
    tag => tag.type === PageSeoTagType.TITLE,
  )
  const additionalMetaHasDescription = additionalMeta?.some(
    tag =>
      tag.type === PageSeoTagType.META &&
      tag.properties?.some(
        property => property.key === 'name' && property.value === 'description',
      ),
  )

  return [
    !additionalMetaHasTitle ? (
      <title
        key="title"
        // Adding data-rh to signal react-helmet-async that this tag is managed by react-helmet-async
        data-rh="true"
      >
        {title}
      </title>
    ) : (
      <Fragment key="title"></Fragment>
    ),
    description && !additionalMetaHasDescription ? (
      <meta
        // Adding data-rh to signal react-helmet-async that this tag is managed by react-helmet-async
        data-rh="true"
        key="description"
        name="description"
        content={description}
      />
    ) : (
      <Fragment key="description"></Fragment>
    ),
    ...(additionalMeta?.map((tag, index) => {
      try {
        return createElement(tag.type.toLowerCase().trim(), {
          ...tag.properties.reduce((props, keyValue) => {
            return {
              ...props,
              [keyValue.key]: keyValue.value,
              // Adding data-rh to signal react-helmet-async that this tag is managed by react-helmet-async
              'data-rh': 'true',
            }
          }, {}),
          // eslint-disable-next-line react/no-array-index-key
          key: index,
        })
      } catch (error) {
        logger.error('error parsing additional meta tags', { tag, error })
      }

      return null
    }) ?? []),
  ]
}
